import _ from 'lodash';
import { WizardStepConstants, WizardConstants } from 'wni-portals-config-js';
import WniProductsUtil from '../AccountAndProduct/WniProductsUtil';

const {
    CPP_PRODUCT_CODE,
    IM_PRODUCT_CODE,
    GL_PRODUCT_CODE,
    CR_PRODUCT_CODE,
    getLobName,
    getPolicyLinePattern
} = WniProductsUtil;

const INVALID_PAGE_INDEX = -1;

const LOB_NAME = getLobName(CPP_PRODUCT_CODE);
const IM_LOB_NAME = getLobName(IM_PRODUCT_CODE);
const GL_LOB_NAME = getLobName(GL_PRODUCT_CODE);
const CR_LOB_NAME = getLobName(CR_PRODUCT_CODE);

/**
 * 
 * @author Fengjuan.Guo@wnins.com
 * @param {*} steps 
 * @param {*} sectionTitleStep 
 * @returns 
 */
function flattenWizarSteps(steps, sectionTitleStep) {
    const newSteps = _.cloneDeep(steps);
    return newSteps.reduce((allSteps, currentStep) => {
        if (currentStep.pages && _.isArray(currentStep.pages)) {
            const currentStepTitle = _.omit(currentStep, 'pages');
            return [...allSteps, ...flattenWizarSteps(currentStep.pages, currentStepTitle)]
        };

        if (sectionTitleStep) {
            _.set(currentStep, WizardConstants.parentTitle, sectionTitleStep.title);
            _.set(currentStep, WizardConstants.parentId, sectionTitleStep.id);
        };

        // stepVisible might have already been set in initialSteps
        if (_.isUndefined(currentStep[WizardConstants.stepVisible])) {
            _.set(currentStep, WizardConstants.stepVisible, true); // to be used by filterVisibleSteps() below
        }

        return [...allSteps, currentStep]
    }, []);
}

/**
 * 
 * @author Fengjuan.Guo@wnins.com
 * @param {*} steps 
 * @returns 
 */
function filterVisibleSteps(steps) {
    if (_.isEmpty(steps)) {
        return [];
    }
    const visibleSteps = steps.filter((step) => step[WizardConstants.stepVisible]);
    return visibleSteps;
}


/**
 * Get pageIndex from current pageInfo provided
 * @param {string | number} pageInfo the pageIndex or pageId
 * @param {array} wizardSteps
 * @returns {number}
 */
function getWizardPageIndex(pageInfo, wizardSteps = []) {
    // if (Number.isInteger(pageInfo)) {    //not supported by IE
    if (_.isNumber(pageInfo)) {
        return pageInfo;
    }
    let pageIndex = INVALID_PAGE_INDEX;
    if (pageInfo) { // pageId
        pageIndex = wizardSteps.findIndex((step) => step.id === pageInfo);
    }
    return pageIndex;
}


/**
 * Get QuotePageIndex, which is described by th "isQuotePgae" property in pa-wizard-config.json
 * EXAMPLE in the wizard config file:
 * {
 *   "id": "PAQuotePage",
 *   "title": { "id":"quoteandbind.wizard.step.Quote", "defaultMessage": "Quote" },
 *   "path": "/quote",
 *   "component": "PAQuotePage",
 *   "stepProps": {
 *        "template": "WizardPageTemplateWithTitle"
 *   },
 *   "isQuotePage": true
 * }
 * @param {array} wizardSteps
 * @returns {number} page index of the quote page
 */
function getQuotePageIndex(wizardSteps) {
    const flattenSteps = flattenWizarSteps(wizardSteps);
    const visibleSteps = filterVisibleSteps(flattenSteps);
    const retval = visibleSteps.findIndex((step) => step[WizardStepConstants.isQuotePage]);
    return retval;
}


/**
 * Checks whether the Wizard should stay on current page
 * @param {Object} param0 wizard data info
 * @returns {boolean}
 */
function shouldStayOnCurrentPage({
    periodStatus,
    currentStepIndex,
    lastExitWizardPageIndex,
    quotePageIndex,
    isQuotePageExit = false
}) {
    if (periodStatus !== 'Draft') {
        return false;
    }

    let furthestPageIndex = Math.min(lastExitWizardPageIndex, quotePageIndex - 1);
    if(isQuotePageExit) {
        furthestPageIndex = Math.min(lastExitWizardPageIndex, quotePageIndex);
    }
    return currentStepIndex >= furthestPageIndex;
}

function getPolicyLinePatterns(lobData) {
    return _.get(lobData, 'commercialPackage.coverables.lineSelection.policyLinePatterns', []);
};

function isQualificationAvailableForCPP(policyLinePatterns = []) {
    const selectedLinePatternIds = policyLinePatterns.filter((item) => item.checked).map((v) => v.publicId);
    return !_.isEmpty(_.intersection(['GL7Line'], selectedLinePatternIds)) || !_.isEmpty(_.intersection(['CR7Line'], selectedLinePatternIds));
};

function isCRQualificationAvailableForCPPChange(policyLinePatterns = []) {
    const crLinePatternMatch = _.find(policyLinePatterns, (item) => item.publicId === 'CR7Line');
    const isCRFollowSubmission = _.get(crLinePatternMatch, 'editable', false) && _.get(crLinePatternMatch, 'checked', false);
    return isCRFollowSubmission;
}

function isGLQualificationAvailableForCPPChange(policyLinePatterns = []) {
    const glLinePatternMatch = _.find(policyLinePatterns, (item) => item.publicId === 'GL7Line');
    const isGLFollowSubmission = _.get(glLinePatternMatch, 'editable', false) && _.get(glLinePatternMatch, 'checked', false);
    return isGLFollowSubmission
}

function isQualificationAvailableForCPPChange(policyLinePatterns = []) {
    return isCRQualificationAvailableForCPPChange(policyLinePatterns) || isGLQualificationAvailableForCPPChange(policyLinePatterns);
}

function skipLinesForCPP(lobData) {
    const selectedLines = getPolicyLinePatterns(lobData)?.filter((item) => item.checked).map((v) => v.publicId);
    const shouldHideLines = WniProductsUtil.policyLinePatternForCPP?.filter((item) => !_.includes(selectedLines, item.policyLinePattern));
    const shouldHideParentIds = shouldHideLines.map((item) => `CPP_${_.upperFirst(item.lobName)}`);
    return shouldHideParentIds;
};

function isModifiersPageAvailable(baseData) {
    const {
        source_Ext: source
    } = baseData;
    return !_.includes(['PolicySystem', 'SIPolicySystem'], source);
};

const getSkipPagesForCPP = (submission = {}) => {
    const {
        lobData,
        baseData
    } = submission
    // skip cpp lines
    const skipLineIds = skipLinesForCPP(lobData)
    const skipLines = skipLineIds.map((item) => {
        return {
            [WizardConstants.parentId]: item,
            [WizardConstants.stepVisible]: false
        }
    });
    // skip QualificationPage or not
    const policyLinePatterns = getPolicyLinePatterns(lobData);
    const isQualificationAvailable = isQualificationAvailableForCPP(policyLinePatterns);

    // skip unmanned Airfact page or not  GLUnmannedAircraftPage
    const isUnmannedAircraftAvailable = _.get(lobData, `${GL_LOB_NAME}.isUnmannedAircraftAvailable`, false);

    // skip commercial crime page or not
    const isCommercialCrimeAvailable = _.get(lobData, `${CR_LOB_NAME}.coverableSelection.visibleOfCommercialCrime`, false);

    // skip Commercial Fidelity page or not
    const isCommercialFidelityAvailable = _.get(lobData, `${CR_LOB_NAME}.coverableSelection.visibleOfCommercialFidelity`, false);

    // skip CPP Modifiers Page or not
    const selectedData = policyLinePatterns.filter((v) => v.checked);
    const onlyHasCRProduct = selectedData.length === 1 && selectedData.find((v) => v.publicId === getPolicyLinePattern(CR_PRODUCT_CODE));
    const isModifiersAvailable = !(onlyHasCRProduct && _.isEmpty(_.get(lobData, `${CR_LOB_NAME}.modifiers_Ext`, [])))
    const isModifiersAvailableBySource = isModifiersPageAvailable(baseData);

    return [
        ...skipLines,
        { [WizardConstants.stepId]: 'CPPQualificationPage', [WizardConstants.stepVisible]: isQualificationAvailable },
        { [WizardConstants.stepId]: 'GLUnmannedAircraftPage', [WizardConstants.stepVisible]: isUnmannedAircraftAvailable },
        { [WizardConstants.stepId]: 'CRCommercialCrimePage', [WizardConstants.stepVisible]: isCommercialCrimeAvailable },
        { [WizardConstants.stepId]: 'CRCommercialFidelityPage', [WizardConstants.stepVisible]: isCommercialFidelityAvailable },
        { [WizardConstants.stepId]: 'CPPModifiersPage', [WizardConstants.stepVisible]: isModifiersAvailable && isModifiersAvailableBySource },
    ]

};

const skipExsitingStepsFilter = (policyLinePatterns = [], stepMaps = []) => {

    const selectedLinesInNB = policyLinePatterns.filter((item) => item.checked && !item.editable);
    const selectedLinesInNBIds = selectedLinesInNB.map((item) => {
        const lobName = WniProductsUtil.getPolicyLobName(item.publicId);
        return `CPP_${_.upperFirst(lobName)}`;
    });

    const unVisibleForExsitingSteps = stepMaps.filter((item) => !item.isVisibleForExsiting && _.includes(selectedLinesInNBIds, item[WizardConstants.parentId]));
    return unVisibleForExsitingSteps.map((item) => {
        return { [WizardConstants.stepId]: item.id, [WizardConstants.stepVisible]: false }
    });
};

const getSkipPagesForCPPChange = (submission = {}, stepMaps = []) => {
    const {
        lobData,
        baseData,
        changeData
    } = submission;

    // skip cpp unSelected lines
    const skipLineIds = skipLinesForCPP(lobData)
    const skipLines = skipLineIds.map((item) => {
        return {
            [WizardConstants.parentId]: item,
            [WizardConstants.stepVisible]: false
        }
    });

    // skip QualificationPage or not
    const policyLinePatterns = getPolicyLinePatterns(lobData);
    const skipExsitingSteps = skipExsitingStepsFilter(policyLinePatterns, stepMaps);

    const glLinePatternMatch = _.find(policyLinePatterns, (item) => item.publicId === 'GL7Line');
    const isGLFollowSubmission = _.get(glLinePatternMatch, 'editable', false);

    const isQualificationAvailable = isQualificationAvailableForCPPChange(policyLinePatterns);

    // skip unmanned Airfact page or not  GLUnmannedAircraftPage
    const isUnmannedAircraftAvailable = _.get(lobData, `${GL_LOB_NAME}.isUnmannedAircraftAvailable`, false) && isGLFollowSubmission;

    // skip commercial crime page or not
    const isCommercialCrimeAvailable = _.get(lobData, `${CR_LOB_NAME}.coverableSelection.visibleOfCommercialCrime`, false);

    // skip Commercial Fidelity page or not
    const isCommercialFidelityAvailable = _.get(lobData, `${CR_LOB_NAME}.coverableSelection.visibleOfCommercialFidelity`, false);

    const isModifiersAvailableBySource = isModifiersPageAvailable(baseData);
    const newPCNSelectedProducts = policyLinePatterns.filter((v) => v.checked && v.editable);
    const isCPPPCNModifiersPageVisible = !_.isEmpty(newPCNSelectedProducts) && isModifiersAvailableBySource;
    const isBillingOptionsEditable = _.get(changeData, 'isBillingOptionsEditable', false);

    return [
        ...skipExsitingSteps,
        ...skipLines,
        { [WizardConstants.stepId]: 'CPPQualificationPage', [WizardConstants.stepVisible]: isQualificationAvailable },
        { [WizardConstants.stepId]: 'GLUnmannedAircraftPage', [WizardConstants.stepVisible]: isUnmannedAircraftAvailable },
        { [WizardConstants.stepId]: 'CRCommercialCrimePage', [WizardConstants.stepVisible]: isCommercialCrimeAvailable },
        { [WizardConstants.stepId]: 'CRCommercialFidelityPage', [WizardConstants.stepVisible]: isCommercialFidelityAvailable },
        { [WizardConstants.stepId]: 'CPPModifiersPage', [WizardConstants.stepVisible]: isCPPPCNModifiersPageVisible },
        { [WizardConstants.stepId]: 'CPPPaymentPage', [WizardConstants.stepVisible]: isBillingOptionsEditable }
    ]
}


export default {
    getWizardPageIndex,
    getQuotePageIndex,
    shouldStayOnCurrentPage,
    getPolicyLinePatterns,
    isQualificationAvailableForCPP,
    isCRQualificationAvailableForCPPChange,
    isGLQualificationAvailableForCPPChange,
    isQualificationAvailableForCPPChange,
    skipLinesForCPP,
    //
    flattenWizarSteps,
    filterVisibleSteps,
    isModifiersPageAvailable,
    getSkipPagesForCPP,
    getSkipPagesForCPPChange
};
